import { Component, Input, Output, EventEmitter, HostListener, ViewChild, Renderer2, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, inject } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import {OffsetService} from "../../route-planner/planner/shared/services/offset/offset.service";

@UntilDestroy()
@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DropdownComponent implements AfterViewInit, ControlValueAccessor {
    private readonly renderer = inject(Renderer2);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly offsetService = inject(OffsetService);
    control = inject(NgControl, { self: true, optional: true });

    public static CustomSelectOpenedClass = 'custom-select-opened';

    get value() {
        return this._value;
    }

    set value(value) {
        this._value = value;
        this._onChange(value);
    }

    @Input() public items: any[];
    @Input() public placeholder: string;
    @Input() public errorText: string;
    @Input() public loading: boolean;
    @Input() public multiple: boolean;
    @Input() public bindLabel = 'label';
    @Input() public bindValue = 'value';
    @Input() public disable = false;
    @Input() public closeOnScroll = true;

    @Output() public change = new EventEmitter();
    @Output() public clear = new EventEmitter();
    @Output() public open = new EventEmitter();
    @Output() public close = new EventEmitter();
    @Output() public blur = new EventEmitter();
    @Output() public focus = new EventEmitter();

    @ViewChild(NgSelectComponent) private readonly ngSelectComponent: NgSelectComponent;
    @ViewChild(PopoverDirective) private popoverDirective: PopoverDirective;

    private _value: any;

    constructor() {
        if (this.control) {
            this.control.valueAccessor = this;
        }
    }

    ngAfterViewInit(): void {
        this.popoverDirective.hide();
        if (this.control) {
            this.control.statusChanges.pipe(untilDestroyed(this)).subscribe(status => {
                if (status === 'INVALID') {
                    this.ngSelectComponent.focus();
                    this.popoverDirective.show();
                } else {
                    this.popoverDirective.hide();
                }
            });
        }
    }

    @HostListener('window:wheel') onScrollEvent() {
        if (this.closeOnScroll) {
            this.ngSelectComponent.close();
        }
    }

    public registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    public writeValue(value: any): void {
        this.value = value;
        this.changeDetectorRef.markForCheck();
    }

    public onChange(newValue: any): void {
        this.change.emit(newValue);
    }

    public onClear(): void {
        this.clear.emit();
    }

    public onOpen($event: any): void {
        this.renderer.addClass(this.ngSelectComponent.element, DropdownComponent.CustomSelectOpenedClass);
        this.open.emit($event);
        setTimeout(() => {
            this.offsetWithZoom();
        }, 0);
    }

    public onClose($event: any): void {
        this._onTouched();
        this.close.emit($event);
    }

    public onBlur($event: any): void {
        this._onTouched();
        this.blur.emit($event);
        this.renderer.removeClass(this.ngSelectComponent.element, DropdownComponent.CustomSelectOpenedClass);
    }

    public onFocus($event: any): void {
        this.focus.emit($event);
    }

    public getItemLabel(item: any): string {
        return item ? item[this.bindLabel] : null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private _onChange = (_: any) => {};
    private _onTouched = () => {};

    private offsetWithZoom(): void {
        this.offsetService.setOffset("ng-dropdown-panel", this.ngSelectComponent.element);
    }
}
